import { useCallback, useMemo, useState } from "react";
import type { GridFilter, GridLoader, GridQuery, LayerTablePage } from "@fortex/api";
import { catalogueEntryOf } from "../../catalogue/types";
import type { InfoState } from "../../core/store";
import { format } from "../../i18n/labels";
import { zoomToOpenFeature } from "../featureZoom";
import { useCatalogue, useInfo, useLabels, useLayers, useMapCore, useMapStore } from "../hooks";
import { Icon } from "../icons";
import { breadcrumb } from "../layerTreeModel";
import { Button } from "../primitives/Button";
import { DataGrid, englishGridLabels, hungarianGridLabels } from "../primitives/DataGrid";
import type { GridColumn } from "../primitives/DataGrid";
import { Input } from "../primitives/Input";
import { Modal } from "../primitives/Modal";

export type LayerTableRow = Record<string, unknown>;

export const idColumn = "Id";

export function tableColumns(columns: string[]): GridColumn<LayerTableRow>[] {
  return columns.map(name => ({
    key: name,
    header: name,
    field: name,
    sortable: true,
    mono: /^(id|validfrom|validto)$/i.test(name),
    filter: { kind: /date|valid/i.test(name) ? "date" : "text" }
  }));
}

export function rowId(row: LayerTableRow): string {
  const value = row[idColumn] ?? row[idColumn.toLowerCase()] ?? row[idColumn.toUpperCase()];

  return value === null || value === undefined ? JSON.stringify(row) : String(value);
}

export function dateFilters(from: string, to: string): GridFilter[] {
  const result: GridFilter[] = [];

  if (from.length > 0) result.push({ fieldName: "ValidFrom", type: "GreaterThanOrEqual", value: from });
  if (to.length > 0) result.push({ fieldName: "ValidFrom", type: "LessThanOrEqual", value: to });

  return result;
}

export function csvOf(columns: string[], rows: LayerTableRow[]): string {
  const escape = (value: unknown) => {
    const text = value === null || value === undefined ? "" : String(value);

    return /[";\n]/.test(text) ? `"${text.replace(/"/g, "\"\"")}"` : text;
  };

  return [columns.join(";"), ...rows.map(row => columns.map(a => escape(row[a])).join(";"))].join("\n");
}

export function downloadText(name: string, text: string): void {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");

  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export type LayerTablePanelProps = { className?: string };

type LayerTableGridProps = {
  layerName: string;
  layerNames: string[];
  title: string;
  modal: boolean;
  onModal: (modal: boolean) => void;
  onClose: () => void;
  className?: string;
};

export function selectedRowId(state: InfoState, layerNames: string[]): string | null {
  const hit = state.kind === "tile" ? state.feature.hit : state.kind === "loading" ? state.hit : null;

  return hit !== null && layerNames.includes(hit.layer) ? hit.id : null;
}

function LayerTableGrid({ layerName, layerNames, title, modal, onModal, onClose, className }: LayerTableGridProps) {
  const core = useMapCore();
  const labels = useLabels();
  const { state: info } = useInfo();
  const langId = useMapStore(a => a.langId);
  const [columns, setColumns] = useState<string[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [rows, setRows] = useState<LayerTableRow[]>([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [dates, setDates] = useState<GridFilter[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const gridLabels = langId === 2 ? englishGridLabels : hungarianGridLabels;

  const load = useCallback<GridLoader<LayerTableRow>>(
    async (query, signal) => {
      const result: LayerTablePage = await core.api.layerTable(layerName, query, signal);

      setColumns(result.columns);
      setTotal(result.totalCount);

      return { rows: result.rows, total: result.totalCount };
    },
    [core, layerName]
  );

  const gridColumns = useMemo(() => tableColumns(columns), [columns]);
  const onRowsChange = useCallback((next: LayerTableRow[]) => setRows(next), []);
  const locate = useCallback((key: string, query: GridQuery, signal: AbortSignal) => core.api.layerTablePosition(layerName, key, query, signal), [core, layerName]);
  const selectedId = selectedRowId(info, layerNames);

  const openRow = async (row: LayerTableRow) => {
    const id = rowId(row);

    setActiveId(id);
    await core.info.openFeature(layerName, id);
    zoomToOpenFeature(core);
  };

  const toolbar = (
    <>
      <Button kind="ghost" size="sm" icon="redo" label={labels.refresh} onClick={() => setReloadToken(a => a + 1)} />
      <Button kind="ghost" size="sm" icon="save" label={labels.exportCsv} disabled={rows.length === 0} onClick={() => downloadText(`${layerName}.csv`, csvOf(columns, rows))} />
      {columns.some(a => a.toLowerCase() === "validfrom") ? (
        <span className="fxmap-table-dates">
          <Input type="date" value={from} aria-label={labels.changedFrom} onChange={event => setFrom(event.target.value)} />
          <Input type="date" value={to} aria-label={labels.changedTo} onChange={event => setTo(event.target.value)} />
          <Button kind="ghost" size="sm" label={labels.apply} onClick={() => setDates(dateFilters(from, to))} />
        </span>
      ) : null}
    </>
  );

  const subtitle = total !== null ? format(labels.tableRows, { count: total }) : "";
  const grid = <DataGrid<LayerTableRow> columns={gridColumns} rowKey={rowId} load={load} labels={gridLabels} defaultSortField={idColumn} pageSizes={[15, 25, 50, 100]} toolbar={toolbar} externalFilters={dates} reloadToken={reloadToken} selectable activeKey={selectedId ?? activeId} revealKey={selectedId} locate={locate} onRowClick={row => void openRow(row)} onRowsChange={onRowsChange} dense dataFxmap="layer-table" />;

  if (modal) {
    return (
      <Modal open title={title} subtitle={subtitle} onClose={onClose} closeLabel={labels.close} width="min(1200px, calc(100% - 32px))" headerTools={<Button kind="icon" size="sm" icon="exitFullscreen" label={labels.backToPanel} onClick={() => onModal(false)} />} dataFxmap="layer-table-modal" className="fxmap-table-modal">
        {grid}
      </Modal>
    );
  }

  return (
    <section className={["fxmap-table-panel", "fxmap-card", className ?? ""].filter(Boolean).join(" ")} aria-label={labels.layerTable} data-fxmap="layer-table-panel">
      <header className="fxmap-table-panel-header">
        <Icon name="grid" className="fxmap-side-header-icon" />
        <div className="fxmap-table-panel-titles">
          <h2 className="fxmap-side-title fxmap-display fxmap-truncate">{title}</h2>
          <span className="fxmap-muted fxmap-mono fxmap-truncate">{subtitle}</span>
        </div>
        <Button kind="icon" size="sm" icon="fullscreen" label={labels.openInWindow} onClick={() => onModal(true)} />
        <Button kind="icon" size="sm" icon="close" label={labels.close} onClick={onClose} data-fxmap-action="close" />
      </header>
      <div className="fxmap-table-panel-body">{grid}</div>
    </section>
  );
}

export function LayerTablePanel({ className }: LayerTablePanelProps) {
  const core = useMapCore();
  const tableLayer = useMapStore(a => a.tableLayer);
  const catalogue = useCatalogue();
  const { list } = useLayers();
  const [modal, setModal] = useState(false);
  const snapshot = tableLayer === null ? null : (list.find(a => a.id === tableLayer) ?? null);
  const entry = snapshot === null ? null : catalogueEntryOf(catalogue, snapshot);
  const layerName = entry?.idName ?? null;

  if (snapshot === null || layerName === null) return null;

  return <LayerTableGrid key={layerName} layerName={layerName} layerNames={[layerName, snapshot.id, snapshot.key]} title={entry !== null ? breadcrumb(entry) : snapshot.title} modal={modal} onModal={setModal} onClose={() => core.store.getState().patch({ tableLayer: null })} className={className} />;
}
